/* One dialog, used by everything that asks a short question.
 *
 * There was already a modal on the day screen — the keyboard-shortcut sheet —
 * and the moment a second one was needed the choice was to copy it or to make
 * it a component. Copying is how two dialogs end up with two different ideas
 * about what Escape does.
 *
 * What a dialog owes somebody, and what this handles so no caller has to
 * remember it:
 *
 *   · **A name.** `aria-labelledby` pointing at its own heading, so it is
 *     announced as "Move this block, dialog" rather than as "dialog".
 *   · **Focus, taken and given back.** The first field gets focus on open; the
 *     control that opened it gets focus back on close. A dialog that drops you
 *     at the top of the document has lost your place.
 *   · **Focus, kept.** Tab cycles inside it. A modal you can tab out of, into
 *     a page a screen reader has been told is inert, is a trap of the other
 *     kind.
 *   · **Escape, and only from the top.** Closing on a click of the backdrop as
 *     well, because that is what people try.
 *
 * Deliberately not a `<dialog>` element: `showModal()` has the behaviour but
 * its default styling and the top-layer interaction with the toast stack cost
 * more than the twenty lines below.
 */

import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import Icon from './Icon';
import type { IconName } from './Icon';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Dialog({ title, icon, description, onClose, children, footer }: {
  title: string;
  /** A picture for what kind of question this is — a clock for "when", a bin
      for a deletion. Beside the title, never instead of it. */
  icon?: IconName;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const id = useId();

  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null;

    /* The first FIELD, not the first button. Document order would hand focus
       to the ✕ in the corner, and a dialog that asks "when?" should open with
       the cursor in the answer rather than on the way out. */
    const field = ref.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), select, textarea');
    (field ?? ref.current?.querySelector<HTMLElement>('button'))?.focus();

    return () => {
      /* Back where it came from. When it no longer exists — the row that
         opened this dialog has just been re-rendered by the change the dialog
         made — focus goes to the main region rather than to the body, so the
         next Tab carries on from the content instead of from the top of the
         document. */
      if (opener.current?.isConnected) { opener.current.focus(); return; }
      document.getElementById('main')?.focus();
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab') return;

    /* Visible, judged without asking for layout. `offsetParent` was the
       obvious check and is the wrong one: it is null for anything inside a
       `position: fixed` ancestor in some engines, and null for *everything*
       in a test environment with no layout at all — which quietly turned the
       trap off in exactly the place that was meant to prove it worked. */
    const items = [...(ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])]
      .filter(el => !el.hasAttribute('hidden') && !el.closest('[aria-hidden="true"]'));
    if (!items.length) return;

    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div ref={ref} className="modal" role="dialog" aria-modal="true"
           aria-labelledby={`${id}-title`}
           aria-describedby={description ? `${id}-desc` : undefined}
           onClick={e => e.stopPropagation()}
           onKeyDown={onKeyDown}>
        <div className="modal-head">
          {icon && <span className="section-icon" aria-hidden="true"><Icon name={icon} size={18} /></span>}
          <h2 id={`${id}-title`}>{title}</h2>
          <button className="btn btn-ghost btn-icon modal-close" onClick={onClose}
                  aria-label="Close" title="Close"><Icon name="close" /></button>
        </div>

        {description && <p className="muted small" id={`${id}-desc`}>{description}</p>}

        {children}

        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
